import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReconciledItem,
  ReconciliationReport,
  UnclearedBookItem,
  UnexplainedBankItem,
} from '../clients/rust-api-client';
import { ledgerService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import { Divider } from '../common/Catalyst/divider';
import { Subheading } from '../common/Catalyst/heading';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  absMoney,
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
  sameDecimal,
} from '../utils/billingHelpers';
import PostBankItemDialog from './PostBankItemDialog';

interface Props {
  hoaId: string;
  reconciliationId: string;
  canReconcile: boolean;
  canPostBankActivity: boolean;
}

/**
 * How a matched item was made, and therefore how much to doubt it.
 *
 * Three states, not two. `matched_automatically` is `true` both for the
 * matcher's proposals and for a line posted from the very transaction
 * it clears, because no person judged either — so `source_type` is what
 * tells them apart. A bank-activity line is not a proposal to check:
 * it was created *from* that bank transaction and cannot be mismatched.
 */
const matchOrigin = (
  item: ReconciledItem
): { label: string; color: 'amber' | 'green' | 'sky' } => {
  if (item.source_type === 'bank_activity') {
    return { label: 'from the statement', color: 'sky' };
  }
  return item.matched_automatically
    ? { label: 'proposed', color: 'amber' }
    : { label: 'confirmed', color: 'green' };
};

/** A proposal nobody has confirmed yet — the month-end to-do list. */
const isUnreviewed = (item: ReconciledItem): boolean =>
  item.matched_automatically && item.source_type !== 'bank_activity';

const ReconciliationWorkspace: React.FC<Props> = ({
  hoaId,
  reconciliationId,
  canReconcile,
  canPostBankActivity,
}) => {
  const navigate = useNavigate();
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pickedLine, setPickedLine] = useState<string | null>(null);
  const [pickedTxn, setPickedTxn] = useState<string | null>(null);
  const [posting, setPosting] = useState<UnexplainedBankItem | null>(null);
  const [confirmComplete, setConfirmComplete] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await ledgerService.getReconciliation({
        hoaId,
        reconciliationId,
      });
      setReport(data);
    } catch (err) {
      console.error('Error loading reconciliation:', err);
      setError(billingErrorMessage(err, "Couldn't load this reconciliation."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, reconciliationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const clearSelection = () => {
    setPickedLine(null);
    setPickedTxn(null);
  };

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    try {
      setBusy(true);
      setError(null);
      await fn();
      clearSelection();
      await load();
    } catch (err) {
      setError(billingErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const match = () => {
    if (!pickedLine || !pickedTxn) return;
    void act(
      () =>
        ledgerService.matchReconciliationItem({
          hoaId,
          reconciliationId,
          reconciliationMatch: {
            journal_line_id: pickedLine,
            bank_transaction_id: pickedTxn,
          },
        }),
      "Couldn't match those two."
    );
  };

  const unmatch = (itemId: string) =>
    void act(
      () =>
        ledgerService.unmatchReconciliationItem({
          hoaId,
          reconciliationId,
          itemId,
        }),
      "Couldn't undo that match."
    );

  const complete = () =>
    void act(async () => {
      await ledgerService.completeReconciliation({
        hoaId,
        reconciliationId,
      });
      setConfirmComplete(false);
    }, "Couldn't complete this reconciliation.");

  if (loading) return <LoadingSpinner />;
  if (!report) return null;

  const done = report.status === 'completed';
  const editable = canReconcile && !done;
  const balanced = sameDecimal(report.difference, '0');
  const unreviewed = report.matched.filter(isUnreviewed).length;

  /**
   * A hint, not a filter. Selecting on one side highlights equal
   * amounts on the other — but the amounts that actually need a person
   * are exactly the ones that do NOT match cleanly, so hiding the rest
   * would hide the work.
   */
  const echoesLine = (item: UnclearedBookItem): boolean =>
    !!pickedTxn &&
    !!report.unexplained_bank_items.find(
      (t) =>
        t.bank_transaction_id === pickedTxn &&
        sameDecimal(absMoney(t.amount), absMoney(item.amount))
    );

  const echoesTxn = (item: UnexplainedBankItem): boolean =>
    !!pickedLine &&
    !!report.uncleared_book_items.find(
      (l) =>
        l.journal_line_id === pickedLine &&
        sameDecimal(absMoney(l.amount), absMoney(item.amount))
    );

  const rowClass = (picked: boolean, echoed: boolean): string =>
    [
      'flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm text-zinc-950 dark:text-white cursor-pointer',
      picked
        ? 'bg-sky-100 dark:bg-sky-900/40 ring-1 ring-sky-400'
        : echoed
          ? 'bg-amber-50 dark:bg-amber-900/20'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800',
    ].join(' ');

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="That did not work"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <Subheading>
            {report.account_number} {report.account_name}
          </Subheading>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            Statement {formatBillingDate(report.statement_date)}
            {done && ' · completed'}
          </p>
        </div>
        <Button
          plain
          onClick={() => navigate(`/hoa/${hoaId}/accounting/reconciliations`)}
        >
          All reconciliations
        </Button>
      </div>

      <Divider className="my-5" />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* The arithmetic, pinned. Difference is the only number that
            matters and is never behind a scroll. */}
        <div className="text-sm text-zinc-950 dark:text-white">
          <dl className="space-y-1.5">
            <div className="flex justify-between gap-4">
              <dt>Statement balance</dt>
              <dd className="tabular-nums">
                {formatMoney(report.statement_balance)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>+ Deposits in transit</dt>
              <dd className="tabular-nums">
                {formatMoney(report.deposits_in_transit)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>− Outstanding items</dt>
              <dd className="tabular-nums">
                {formatMoney(report.outstanding_items)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-zinc-200 dark:border-zinc-700 pt-1.5">
              <dt>= Adjusted balance</dt>
              <dd className="tabular-nums">
                {formatMoney(report.adjusted_balance)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt>Book balance</dt>
              <dd className="tabular-nums">
                {formatMoney(report.book_balance)}
              </dd>
            </div>
            <div
              className={[
                'flex justify-between gap-4 border-t border-zinc-200 dark:border-zinc-700 pt-1.5 font-semibold',
                balanced
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400',
              ].join(' ')}
            >
              <dt>= Difference</dt>
              <dd className="tabular-nums" data-testid="difference">
                {formatMoney(report.difference)}
                {balanced ? ' ✓' : ''}
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h4 className="text-sm font-medium mb-2 text-zinc-950 dark:text-white">
            Our postings, uncleared ({report.uncleared_book_items.length})
          </h4>
          <div className="space-y-1">
            {report.uncleared_book_items.map((item) => (
              <div
                key={item.journal_line_id}
                role="button"
                tabIndex={0}
                aria-pressed={pickedLine === item.journal_line_id}
                className={rowClass(
                  pickedLine === item.journal_line_id,
                  echoesLine(item)
                )}
                onClick={() =>
                  editable &&
                  setPickedLine(
                    pickedLine === item.journal_line_id
                      ? null
                      : item.journal_line_id
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (editable)
                      setPickedLine(
                        pickedLine === item.journal_line_id
                          ? null
                          : item.journal_line_id
                      );
                  }
                }}
              >
                <span className="truncate">
                  {formatBillingDate(item.effective_date)} {item.description}
                </span>
                <span className="tabular-nums shrink-0">
                  {formatMoney(item.amount)}
                </span>
              </div>
            ))}
            {report.uncleared_book_items.length === 0 && (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Everything we posted has cleared.
              </p>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium mb-2 text-zinc-950 dark:text-white">
            On the statement, unexplained (
            {report.unexplained_bank_items.length})
          </h4>
          <div className="space-y-1">
            {report.unexplained_bank_items.map((item) => (
              <div
                key={item.bank_transaction_id}
                className={rowClass(
                  pickedTxn === item.bank_transaction_id,
                  echoesTxn(item)
                )}
              >
                <span
                  role="button"
                  tabIndex={0}
                  aria-pressed={pickedTxn === item.bank_transaction_id}
                  className="truncate flex-1"
                  onClick={() =>
                    editable &&
                    setPickedTxn(
                      pickedTxn === item.bank_transaction_id
                        ? null
                        : item.bank_transaction_id
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (editable)
                        setPickedTxn(
                          pickedTxn === item.bank_transaction_id
                            ? null
                            : item.bank_transaction_id
                        );
                    }
                  }}
                >
                  {formatBillingDate(item.posted_on)} {item.description ?? '—'}
                </span>
                <span className="tabular-nums shrink-0">
                  {formatMoney(item.amount)}
                </span>
                {/*
                  `looks_like` is the institution's own wording read as a
                  hint for this dropdown. Nothing posts on the strength
                  of it — a person picks, every time.
                */}
                {editable && canPostBankActivity && (
                  <Button
                    plain
                    className="shrink-0"
                    onClick={() => setPosting(item)}
                  >
                    Post…
                  </Button>
                )}
              </div>
            ))}
            {report.unexplained_bank_items.length === 0 && (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Nothing on the statement is unexplained.
              </p>
            )}
          </div>
        </div>
      </div>

      {editable && (
        <div className="mt-5 flex items-center gap-3">
          <Button
            color="sky"
            disabled={!pickedLine || !pickedTxn || busy}
            onClick={match}
          >
            Match selected
          </Button>
          {(pickedLine || pickedTxn) && (
            <Button plain onClick={clearSelection}>
              Clear selection
            </Button>
          )}
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            Pick one from each side.
          </span>
        </div>
      )}

      <Divider className="my-6" />

      <div className="flex items-center justify-between gap-4">
        <Subheading>Matched ({report.matched.length})</Subheading>
        {editable && (
          <Button
            color="sky"
            disabled={busy}
            onClick={() => setConfirmComplete(true)}
          >
            Complete reconciliation
          </Button>
        )}
      </div>

      <div className="mt-3 space-y-1">
        {report.matched.map((item) => {
          const origin = matchOrigin(item);
          return (
            <div
              key={item.item_id}
              className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-zinc-950 dark:text-white"
            >
              <span className="truncate">
                {formatBillingDate(item.effective_date)} {item.description}
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="tabular-nums">{formatMoney(item.amount)}</span>
                <Badge color={origin.color}>{origin.label}</Badge>
                {editable && (
                  <Button plain onClick={() => unmatch(item.item_id)}>
                    Unmatch
                  </Button>
                )}
              </span>
            </div>
          );
        })}
        {report.matched.length === 0 && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Nothing matched yet.
          </p>
        )}
      </div>

      {posting && (
        <PostBankItemDialog
          hoaId={hoaId}
          reconciliationId={reconciliationId}
          item={posting}
          onClose={() => setPosting(null)}
          onPosted={() => {
            setPosting(null);
            void load();
          }}
        />
      )}

      <Dialog open={confirmComplete} onClose={() => setConfirmComplete(false)}>
        <DialogTitle>Complete this reconciliation?</DialogTitle>
        <DialogBody>
          {/*
            A one-way door, and the dialog says so. `match`, `unmatch`
            and `open` all refuse a completed reconciliation and there is
            no reopen path anywhere in the API — and because the unique
            index on `journal_line_id` is global, a posting cleared by a
            wrong match can never clear again.
          */}
          <p className="text-sm text-zinc-950 dark:text-white">
            This cannot be undone. There is no reopen, and a posting cleared by
            a wrong match can never clear again.
          </p>
          {unreviewed > 0 && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
              {unreviewed} match{unreviewed === 1 ? '' : 'es'} still read{' '}
              <strong>proposed</strong> — the matcher paired equal amounts
              within five days and nobody has confirmed them. Two homeowners
              paying the same dues on the same day is the normal case, so these
              are the ones worth a second look.
            </p>
          )}
          {!balanced && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">
              This account is out by {formatMoney(report.difference)}.
              Completing will be refused: a completed reconciliation claims the
              books and the institution agree.
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setConfirmComplete(false)}>
            Cancel
          </Button>
          <Button color="sky" disabled={busy} onClick={complete}>
            Complete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReconciliationWorkspace;
